/**
 * Shared Framer Motion variants.
 *
 * Why module-level constants instead of inline objects?
 * - Inline `animate={{...}}` creates a NEW object every render. While Framer
 *   Motion does its own value-diffing, exporting frozen module-level constants
 *   guarantees referential stability (zero recomputation, zero GC pressure).
 * - This is functionally equivalent to (and cheaper than) wrapping in
 *   useMemo since these configurations are static.
 *
 * Usage:
 *   import { fadeInUp } from "@/lib/motion-variants";
 *   <motion.div {...fadeInUp} transition={{ delay: 0.2 }}>...</motion.div>
 */

// Fade + translate from below (most common entrance)
export const fadeInUp = Object.freeze({
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
});

// Smaller offset variant
export const fadeInUpSm = Object.freeze({
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
});

// Tiny offset variant
export const fadeInUpXs = Object.freeze({
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
});

// Pure fade
export const fadeIn = Object.freeze({
  initial: { opacity: 0 },
  animate: { opacity: 1 },
});

// Slide + fade from left
export const fadeInLeft = Object.freeze({
  initial: { opacity: 0, x: -20 },
  animate: { opacity: 1, x: 0 },
});

// Slide + fade from right
export const fadeInRight = Object.freeze({
  initial: { opacity: 0, x: 20 },
  animate: { opacity: 1, x: 0 },
});

// Scale up from zero
export const scaleIn = Object.freeze({
  initial: { scale: 0 },
  animate: { scale: 1 },
});

// Soft pop-in (95% scale + fade)
export const scaleInSoft = Object.freeze({
  initial: { opacity: 0, scale: 0.95 },
  animate: { opacity: 1, scale: 1 },
});

// Slide + fade from above (header pattern)
export const fadeInDown = Object.freeze({
  initial: { opacity: 0, y: -20 },
  animate: { opacity: 1, y: 0 },
});

// Tiny upward fade
export const fadeInUpTiny = Object.freeze({
  initial: { opacity: 0, y: 5 },
  animate: { opacity: 1, y: 0 },
});

// Collapsible expand (height + opacity)
export const expandHeight = Object.freeze({
  initial: { opacity: 0, height: 0 },
  animate: { opacity: 1, height: "auto" },
  exit: { opacity: 0, height: 0 },
});

// Common exit variants (for AnimatePresence)
export const fadeOut = Object.freeze({
  exit: { opacity: 0 },
});

export const fadeOutDown = Object.freeze({
  exit: { opacity: 0, y: 20 },
});

export const fadeOutUp = Object.freeze({
  exit: { opacity: 0, y: -10 },
});
